import { ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractHTML5ResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractHTML5ResourceConfigBuilder';
import { AssetUrlPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/AssetUrlPlaybackUtility';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Builder class to create an instance of ResourceConfigurationInterface for playback via asset url on HTML5
 */
export class HTML5AssetUrlResourceConfigBuilder extends AbstractHTML5ResourceConfigBuilder {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'HTML5AssetUrlResourceConfigBuilder',
				...argument
			} );

		// init compositional members
		this.assetPlaybackUtil = new AssetUrlPlaybackUtility( argument );
	}

	/**
	 * @description Returns filled resource configuration for playback via asset url on HTML5
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve, reject ) => {

				// get config prefilled with values for HTML5 playback
				super.getResult().then(
					( data ) => {
						let resource: ResourceConfigurationInterface = data?.resource;

						// set values for asset url playback
						const usingStitcher = this.hasStitcherParams();
						const isLive = this.tag.model.contentUtilities.isLive();

						resource = this.setMediaUrl( resource );
						resource = this.setDrm( resource );
						resource = this.setPlatformSpecificResource( resource );
						if ( !usingStitcher ) {
							// TODO: confirm we don't need this if using stitcher
							resource = this.setAdParameters( resource );
						}
						if ( usingStitcher ) {
							// set ssai={} to enable adbreaks
							resource = this.initSsai( resource );
							// required by avia ad adapter plugin => set live flag for correct adapter selection (Live vs VOD)
							resource = this.setIsLive( resource, isLive );
						}
						resource = this.deepMergeResourceFromSmartTagParams( resource );
						if ( usingStitcher && !isLive ) {
							// After host params merge — honor WITHOUT_RESUME_TIME and globalResumeTime (CP / UEC redirects).
							resource = this.setStartTime( resource );
						}
						resource = this.sanitizeResource( resource ); // sanitize resource
						resource = this.testOverrides( resource );

						resolve( { msg: 'Resource config', resource: resource } );
					}
				).catch(
					( error ) => {
						// HTML5 AssetUrl resource config failed, provide minimal fallback for video playback
						this?.logger?.warn( 'HTML5 AssetUrl resource config failed, using minimal configuration:', error );

						// Create minimal resource as fallback to ensure AssetUrl videos can still play on mobile
						const basicResource = window.avia.createResourceConfig();
						resolve( { msg: 'Resource config (minimal)', resource: basicResource } );
					}
				);
			}
		);
	}

	/**
	 * @description Sets media url in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setMediaUrl( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		return super.setMediaUrl( resource, this.assetPlaybackUtil?.getMediaUrl() );
	}
}